import React from 'react';
import {
  Box,
  Typography,
  alpha,
  useTheme,
  Paper,
} from '@mui/material';
import {
  InsertDriveFile as DocumentIcon,
  PlayCircleOutline as VideoIcon,
  Image as ImageIcon,
  Call as CallIcon,
  Launch as UrlIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import { LocalTemplate, MetaTemplate } from '../../types/templates';
import { getComponentConstraints } from '../../utils/templateUtils';
import { extractTemplateExamples, formatTextWithVariables } from '../../utils/templateVariableLabels';
import { WHATSAPP_BG_IMAGE_URL } from '../../constants/whatsapp';

export interface TemplatePreviewProps {
  template: LocalTemplate | MetaTemplate | null;
  title?: string;
  subtitle?: React.ReactNode;
  containerSx?: object;
  align?: 'left' | 'center' | 'right';
}

const MEDIA_HEIGHT = 140;
const MEDIA_RADIUS = 0.5;

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  template,
  title = 'Template Preview',
  subtitle,
  containerSx = {},
  align = 'center',
}) => {
  const theme = useTheme();
  const c = template ? getComponentConstraints(template) : null;
  
  const exampleVariables = React.useMemo(() => {
    if (template?.components) return extractTemplateExamples(template.components);
    return [];
  }, [template]);

  const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const renderHeader = (headerType?: string | null, headerContent?: string) => {
    const type = headerType?.toLowerCase();
    
    if (!type || type === 'none') return null;

    if (type === 'text') {
      const headerBody = headerContent || 'Header Text';
      return (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            color: '#000000',
            wordBreak: 'break-word',
          }}
        >
          {formatTextWithVariables(headerBody, exampleVariables)}
        </Typography>
      );
    }
    // Media placeholders
    let icon = null;
    let bgColor = '#e2e2e2';
    let text = '';

    if (type === 'image') {
      if (headerContent) {
        return (
          <Box
            component="img"
            src={headerContent}
            alt="Header Image"
            sx={{
              width: '100%',
              height: 160,
              objectFit: 'cover',
              borderRadius: MEDIA_RADIUS,
              mb: 1,
              backgroundColor: '#e2e2e2',
            }}
          />
        );
      }
      icon = <ImageIcon sx={{ fontSize: 48, color: '#999' }} />;
      text = 'Image';
    } else if (type === 'video') {
      if (headerContent) {
        return (
          <Box
            key={headerContent}
            sx={{
              width: '100%',
              aspectRatio: '16/9',
              borderRadius: MEDIA_RADIUS,
              mb: 1,
              backgroundColor: '#000',
              overflow: 'hidden',
            }}
          >
            <Box
              component="video"
              src={headerContent}
              controls
              autoPlay
              muted
              loop
              playsInline
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        );
      }
      icon = <VideoIcon sx={{ fontSize: 48, color: '#999' }} />;
      text = 'Video';
    } else if (type === 'document') {
      if (headerContent) {
        return (
          <Box
            component="a"
            href={headerContent}
            target="_blank"
            rel="noopener noreferrer"
            onDoubleClick={() => window.open(headerContent, "_blank")}
            onClick={() => window.open(headerContent, "_blank")}
            sx={{
              width: '100%',
              height: MEDIA_HEIGHT,
              backgroundColor: '#f5f5f5',
              borderRadius: MEDIA_RADIUS,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              color: '#666',
              textDecoration: 'none',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            <iframe
              src={`${headerContent}#page=1`}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: 'none', borderRadius: MEDIA_RADIUS }}
            />
          </Box>
        );
      }
      icon = <DocumentIcon sx={{ fontSize: 48, color: '#999' }} />;
      text = 'Document';
      bgColor = '#f5f5f5';
    }

    if (icon) {
      return (
        <Box
          sx={{
            width: '100%',
            height: MEDIA_HEIGHT,
            backgroundColor: bgColor,
            borderRadius: MEDIA_RADIUS,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            color: '#666',
          }}
        >
          {icon}
          <Typography variant="caption" sx={{ mt: 1 }}>{text} Placeholder</Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ 
      position: 'sticky', 
      top: 88, 
      alignSelf: 'flex-start',
      width: '100%',
      mb: 2, 
      zIndex: 5,
      ...containerSx 
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
        mb: 2 
      }}>
        {title && (
          <Typography variant="h6" gutterBottom align={align} sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        )}
        
        {subtitle && (
          <Box sx={{ mt: -0.5, textAlign: align }}>
            {subtitle}
          </Box>
        )}
      </Box>

      {!template || !c ? (
        <Box
          sx={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 400,
            mx: align === 'left' ? 0 : align === 'right' ? 'auto 0' : 'auto',
            height: '600px',
            backgroundColor: alpha(theme.palette.grey[100], 0.5),
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            textAlign: 'center'
          }}
        >
          <Box>
            <Box sx={{ fontSize: 48, mb: 2, opacity: 0.3 }}>📱</Box>
            <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 250 }}>
              Select or configure a template to see its preview here
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center' }}>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', alignItems: 'flex-start' }}>
            <Box
              sx={{
                maxWidth: 380,
                width: '100%',
                minHeight: 450,
                maxHeight: 700,
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: '#efeae2',
                backgroundImage: `url(${WHATSAPP_BG_IMAGE_URL})`,
                backgroundSize: '300px',
                backgroundRepeat: 'repeat',
                borderRadius: 4,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05), 0 10px 40px rgba(0,0,0,0.1)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  p: '6px 7px 8px 9px',
                  position: 'relative',
                  maxWidth: '90%',
                  alignSelf: 'flex-start',
                  mb: c.buttons && c.buttons.length > 0 ? 1 : 0,
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1, minWidth: 120 }}>
                  {/* Header */}
                  {renderHeader(c.headerFormat, c.headerContent)}

                  {/* Body */}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#111b21', 
                      fontSize: '14.2px', 
                      lineHeight: '19px',
                      whiteSpace: 'pre-wrap', 
                      wordBreak: 'break-word',
                      mb: c.footerText ? 0.5 : 1.5
                    }}
                  >
                    {formatTextWithVariables(c.bodyText || 'Body text will appear here...', exampleVariables)}
                  </Typography>

                  {/* Timestamp inline if no footer, otherwise below */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.5 }}>
                    <Typography variant="caption" sx={{ color: '#667781', fontSize: '11px' }}>
                      {currentTime}
                    </Typography>
                  </Box>

                  {/* {!c.footerText && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.5 }}>
                      <Typography variant="caption" sx={{ color: '#667781', fontSize: '11px' }}>
                        {currentTime}
                      </Typography>
                    </Box>
                  )} */}

                  {/* Footer (Hiding it for now to hide sent via lyftr ai) */}
                  {/* {c.footerText && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#667781', fontSize: '12px' }}>
                        {c.footerText}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#667781', fontSize: '11px', ml: 2, pb: 0.2 }}>
                        {currentTime}
                      </Typography>
                    </Box>
                  )} */}
                </Box>

                {/* Buttons Output */}
                {c.buttons && c.buttons.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1, mx: '-9px', mb: '-8px' }}>
                    {c.buttons.map((btn: any, index: number) => {
                      let IconClass = ReplyIcon;
                      if (btn.type === 'PHONE_NUMBER' || btn.type === 'CALL') IconClass = CallIcon;
                      if (btn.type === 'URL') IconClass = UrlIcon;

                      return (
                        <Box
                          key={index}
                          sx={{
                            borderTop: '1px solid #f0f2f5',
                            py: 1.2,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            cursor: 'default',
                          }}
                        >
                          <IconClass sx={{ fontSize: 18, color: '#00a884' }} />
                          <Typography sx={{ color: '#00a884', fontSize: '14px', fontWeight: 500 }}>
                            {(btn.type === "CALL" || btn.type === "PHONE_NUMBER") && (
                              <a href={`tel:${btn.phone_number}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit'}}>
                                {btn.text || btn.type}
                              </a>
                            )}
                            {btn.type === "URL" && (
                              <a href={btn.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit'}}>
                                {btn.text || btn.type}
                              </a>
                            )}
                            {btn.type !== "CALL" && btn.type !== "PHONE_NUMBER" && btn.type !== "URL" && (
                              <span>{btn.text || btn.type}</span>
                            )}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TemplatePreview;
